import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getTronEnergyConfig, getTronEnergySummary, type TronEnergyConfig, type TronEnergySummary } from '@/api';
import { useApps, useAppsLoading } from '@/stores';
import { TronEnergySummaryCards } from '@/app/components/tron-energy/TronEnergySummaryCards';
import { TronEnergyOrderTable } from '@/app/components/tron-energy/TronEnergyOrderTable';
import { useTronEnergyOrdersPolling } from '@/app/hooks/useTronEnergyOrdersPolling';
import { useTronEnergyOrderForm } from '@/app/hooks/useTronEnergyOrderForm';
import { BackArrowIcon } from '@/app/components/tron-energy/TronEnergyIcons';
import warningIcon from '@/assets/figma/tron-energy/icon-warning.svg';
import { eqUsdt, formatCompactNumber, formatUsdt, gtUsdt, M, USDT_SIGNIFICANT_DIGITS } from '@/utils/money';
import { scaleEnergyPreset } from '@/utils/scaleEnergyPreset';

export function TronEnergyRental() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const appId = id || '';
  const apps = useApps();
  const appsLoading = useAppsLoading();

  const currentApp = useMemo(() => {
    return apps.find((app) => app.id === appId);
  }, [apps, appId]);

  const [config, setConfig] = useState<TronEnergyConfig | null>(null);
  const [summary, setSummary] = useState<TronEnergySummary | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // 刷新统计与配置
  const loadConfigAndSummary = useCallback(async () => {
    if (!appId) return;
    try {
      const [configRes, summaryRes] = await Promise.all([
        getTronEnergyConfig(appId),
        getTronEnergySummary(appId),
      ]);

      if (configRes.ok) {
        setConfig(configRes.data);
      }
      if (summaryRes.ok) {
        setSummary(summaryRes.data);
      }
    } catch (err) {
      console.error('[TronEnergyRental] Load config/summary error:', err);
    } finally {
      setIsInitialLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    loadConfigAndSummary();
  }, [loadConfigAndSummary]);

  // 3秒轮询订单
  const {
    orders,
    total,
    page,
    limit,
    status,
    keyword,
    isLoading: isOrdersLoading,
    isPollingError,
    setPage,
    setStatus,
    setKeyword,
    refreshImmediately,
  } = useTronEnergyOrdersPolling({ appId, pollInterval: 3000 });

  // 租赁表单
  const {
    energyAmount,
    durationHours,
    rechargeAddress,
    minEnergy,
    addressValid,
    energyValid,
    costEstimate,
    isQuoteLoading,
    canSubmit,
    isSigning,
    isSubmitting,
    setEnergyAmount,
    setRechargeAddress,
    handleSubmit,
  } = useTronEnergyOrderForm({
    appId,
    config,
    onOrderCreated: () => {
      refreshImmediately();
      loadConfigAndSummary();
    },
  });

  const handleDocClick = () => {
    const docsUrl = config?.docsUrl || import.meta.env.VITE_TRON_ENERGY_DOCS_URL;
    if (docsUrl) {
      window.open(docsUrl, '_blank', 'noopener,noreferrer');
    } else {
      toast.info(t('tronEnergy.docs.notConfigured', 'API 文档链接尚未配置'));
    }
  };

  if (!appsLoading && currentApp?.energyRentalService.status === 'unavailable') {
    return <Navigate to="/keys" replace />;
  }

  const fieldInputClass =
    'min-w-px flex-1 rounded-2xl border border-slate-200 bg-white px-3.5 text-base font-semibold text-slate-900 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 dark:border-[#1d293d] dark:bg-[#1a1d1f] dark:text-white dark:focus:border-primary dark:focus:ring-primary';

  return (
    <div className="space-y-6">
      <div className="flex h-[57px] items-center gap-3">
        <Link
          to="/keys"
          aria-label={t('common.backToList')}
          className="relative flex size-6 shrink-0 items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        >
          <BackArrowIcon className="size-6" />
        </Link>
        <h1 className="text-2xl leading-8 font-bold whitespace-nowrap text-slate-900 dark:text-white">
          {t('tronEnergy.rentalTitle')}
        </h1>
      </div>

      <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2">
        <TronEnergySummaryCards
          summary={summary}
          isLoading={isInitialLoading}
          onDocsClick={handleDocClick}
        />

        <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-[#D2F0E1]/5 dark:bg-[#1a1d1f]">
          <div className="flex items-center border-b border-slate-200 p-4 dark:border-[#1d293d]">
            <div className="flex items-center gap-1.5">
              <span className="h-[18px] w-[5px] shrink-0 rounded-full bg-emerald-600 dark:bg-primary" />
              <h2 className="text-lg leading-none font-bold whitespace-nowrap text-slate-900 dark:text-white">
                {t('tronEnergy.form.title', '快速租用能量')}
              </h2>
            </div>
          </div>

          <div className="flex flex-col gap-8 px-4 py-3">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-2.5">
                <div className="flex flex-col items-start gap-2.5 sm:flex-row sm:items-center">
                  <label className="w-[128px] shrink-0 text-base leading-none font-medium text-slate-500 dark:text-slate-400">
                    {t('tronEnergy.form.energyAmount', '租用能量量')}
                  </label>
                  <input
                    type="number"
                    min={minEnergy}
                    step={1000}
                    value={energyAmount || ''}
                    onChange={(e) => setEnergyAmount(M(e.target.value || 0).toNumber())}
                    className={`h-[42px] ${fieldInputClass}`}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:pl-[138px]">
                  {(config?.energyPresets || [65000, 131000, 650000, 1000000]).map((preset) => {
                    const scaled = scaleEnergyPreset(preset, i18n.language);
                    const selected = eqUsdt(energyAmount, preset);
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setEnergyAmount(preset)}
                        className={`flex items-center justify-center rounded-lg px-2 py-1.5 text-sm leading-none font-medium ${
                          selected
                            ? 'bg-emerald-600 text-white dark:bg-primary dark:text-primary-foreground'
                            : 'border border-slate-200 bg-white text-slate-900 dark:border-[#D2F0E1]/5 dark:bg-[#141517] dark:text-white'
                        }`}
                      >
                        {t(`tronEnergy.form.energyPreset.${scaled.unit}`, { value: scaled.value })}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 sm:pl-[138px] dark:text-slate-400">
                  {t('tronEnergy.form.minLimitHint', '下限: {{min}} 能量', {
                    min: formatCompactNumber(minEnergy),
                  })}
                </p>
                {!energyValid && (
                  <p className="text-xs text-red-500 sm:pl-[138px]">
                    {t('tronEnergy.form.minEnergyError', '租用能量需不小于 {{min}}', {
                      min: minEnergy.toLocaleString(),
                    })}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2.5">
                <div className="flex flex-col items-start gap-2.5 sm:flex-row sm:items-center">
                  <label className="w-[128px] shrink-0 text-base leading-none font-medium text-slate-500 dark:text-slate-400">
                    {t('tronEnergy.form.rentDuration', '租用时长')}
                  </label>
                  <div className={`flex min-h-[42px] items-center py-2 leading-5 ${fieldInputClass}`}>
                    {t('tronEnergy.form.fixedDuration', '{{hours}} 小时（当前版本固定）', {
                      hours: durationHours,
                    })}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2.5">
                <div className="flex flex-col items-start gap-2.5 sm:flex-row sm:items-center">
                  <label className="w-[128px] shrink-0 text-base leading-none font-medium text-slate-500 dark:text-slate-400">
                    {t('tronEnergy.form.rechargeAddress', '能量充值地址')}
                  </label>
                  <input
                    type="text"
                    value={rechargeAddress}
                    onChange={(e) => setRechargeAddress(e.target.value)}
                    placeholder={t('tronEnergy.form.addressPlaceholder', '请输入 T 开头的 TRON 接收地址')}
                    className={`h-[42px] ${fieldInputClass} font-mono font-medium placeholder:font-sans placeholder:text-slate-400 ${
                      addressValid === false
                        ? 'border-red-400 focus:border-red-400 focus:ring-red-400'
                        : ''
                    }`}
                  />
                </div>
                {addressValid === false && (
                  <p className="text-xs text-red-500 sm:pl-[138px]">
                    {t('tronEnergy.form.invalidAddress', '请输入有效的 Tron Base58Check 地址')}
                  </p>
                )}

                <div className="flex flex-col gap-4 py-1.5 text-base leading-none font-medium sm:pl-[138px]">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="text-slate-500 dark:text-slate-400">
                      {t('tronEnergy.form.requiredPoints', '所需总积分')}:
                    </span>
                    {isQuoteLoading ? (
                      <Loader2
                        className="size-3.5 animate-spin text-slate-400"
                        aria-label={t('common.loading', '加载中...')}
                      />
                    ) : (
                      <>
                        <span className="text-slate-900 dark:text-white">
                          {formatCompactNumber(costEstimate.requiredPoints)}
                        </span>
                        <span className="text-emerald-600 dark:text-primary">
                          {t('tronEnergy.units.points', '积分')}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="text-slate-500 dark:text-slate-400">
                      {t('tronEnergy.form.deductPoints', '扣除积分')}:
                    </span>
                    {isQuoteLoading ? (
                      <Loader2
                        className="size-3.5 animate-spin text-slate-400"
                        aria-label={t('common.loading', '加载中...')}
                      />
                    ) : (
                      <>
                        <span className="text-slate-900 dark:text-white">
                          {formatCompactNumber(costEstimate.pointsToCharge)}
                        </span>
                        <span className="text-emerald-600 dark:text-primary">
                          {t('tronEnergy.units.points', '积分')}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="text-slate-500 dark:text-slate-400">
                      {t('tronEnergy.form.supplementUsdt', '需要补足 USDT')}:
                    </span>
                    {isQuoteLoading ? (
                      <Loader2
                        className="size-3.5 animate-spin text-slate-400"
                        aria-label={t('common.loading', '加载中...')}
                      />
                    ) : (
                      <>
                        <span className="text-slate-900 dark:text-white">
                          {gtUsdt(costEstimate.usdtToCharge || '0', 0)
                            ? formatUsdt(costEstimate.usdtToCharge, {
                                significantDigits: USDT_SIGNIFICANT_DIGITS,
                              })
                            : '0'}
                        </span>
                        <span className="text-emerald-600 dark:text-primary">USDT</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center gap-3">
              <div className="flex w-full items-start px-3">
                <div className="flex w-full items-center gap-2.5 rounded-xl bg-amber-50 px-3 py-2.5 dark:bg-amber-500/10">
                  <img src={warningIcon} alt="" aria-hidden className="size-6 shrink-0" />
                  <p className="min-w-px flex-1 text-sm leading-[1.4] font-medium text-amber-600 dark:text-amber-500">
                    {t(
                      'tronEnergy.form.disclaimer',
                      '能量到账后无法退款，请在生效后及时使用。实际扣费金额以后端结算为准。',
                    )}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={`flex w-full items-center justify-center gap-2 overflow-hidden rounded-full border p-4 text-base font-medium ${
                  isQuoteLoading || costEstimate.affordable
                    ? 'border-slate-200 bg-emerald-600 text-white hover:bg-emerald-700 dark:border-[#1d293d] dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90 disabled:opacity-50'
                    : 'cursor-not-allowed border-red-500 bg-red-500 text-white dark:border-red-500 dark:bg-red-500'
                }`}
              >
                {(isSigning || isSubmitting || isQuoteLoading) && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {isQuoteLoading
                  ? t('tronEnergy.form.submitButton', '立即租用能量')
                  : !costEstimate.affordable
                    ? t('tronEnergy.form.insufficientBalanceBtn', '余额不足')
                    : isSigning
                      ? t('tronEnergy.form.waitingSignature', '等待钱包签名...')
                      : isSubmitting
                        ? t('tronEnergy.form.submittingOrder', '正在提交订单...')
                        : t('tronEnergy.form.submitButton', '立即租用能量')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 订单明细列表 */}
      <TronEnergyOrderTable
        orders={orders}
        total={total}
        page={page}
        limit={limit}
        status={status}
        keyword={keyword}
        isLoading={isOrdersLoading}
        isPollingError={isPollingError}
        onPageChange={setPage}
        onStatusChange={setStatus}
        onKeywordChange={setKeyword}
        onRefresh={refreshImmediately}
      />
    </div>
  );
}
